import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { SubTabs, panelGet } from './kit';
import Overview from './techpages/Overview';
import PagesTable from './techpages/PagesTable';
import SectionsAccess from './techpages/SectionsAccess';
import UrlsSeo from './techpages/UrlsSeo';
import LabelsCarousel from './techpages/LabelsCarousel';
import Shares from './techpages/Shares';
import PageDrawer, { type PageRef } from './techpages/PageDrawer';
import { type Range, RangeBar, defaultRange } from './techpages/shared';
import type { AdminTargetIn } from './adminNav';

/**
 * /admin → Tech URL pages: the public SEO pages, one per technology
 * (/tech/<university>/<title>) and one per university.
 *  - Overview, Tech pages, University pages: traffic and every tracked click
 *    (sections, CTAs, locked sections, shares, sign-ups, crawler hits), with
 *    filters on everything and a per-page drawer — Backend/admin_tech_pages.py.
 *  - Sections & access, URLs & SEO: what each visitor state sees, the editable
 *    URL structure, SEO templates, rate limits, the legacy-endpoint guard —
 *    Backend/tech_pages.py.
 *  - Labels & carousel: the university labels (URL segment + homepage
 *    carousel name) and the carousel switch — the Universities tab's data.
 *  - Shares: every tracked share link (platform tech cards + the public pages'
 *    share buttons), who shared, the channels, and each open — section "shares"
 *    (Backend/admin_shares.py).
 * All through the panel dispatcher, section "tech_pages" (and "universities"
 * / "db" for the labels).
 *
 * Deep link (admin/adminNav.tsx): /admin?group=tech_pages&tab=tech&target=UTECH-…
 * opens the Tech pages tab with that page's drawer — how Horizon data links
 * here. tab also takes overview | university | shares | sections | urls | labels (or
 * the tab's own name); kind=university with a university code opens a
 * university page's drawer.
 */

const TABS = ['Overview', 'Tech pages', 'University pages', 'Shares', 'Sections & access', 'URLs & SEO', 'Labels & carousel'];
const TRAFFIC = new Set(['Overview', 'Tech pages', 'University pages', 'Shares']);
/** Deep-link tab names → tabs. */
const TAB_PARAM: Record<string, string> = {
  overview: 'Overview', tech: 'Tech pages', tech_pages: 'Tech pages', pages: 'Tech pages',
  university: 'University pages', university_pages: 'University pages', sections: 'Sections & access',
  access: 'Sections & access', urls: 'URLs & SEO', seo: 'URLs & SEO', labels: 'Labels & carousel',
  carousel: 'Labels & carousel', shares: 'Shares', share: 'Shares', share_links: 'Shares',
};

const TechPagesPanel: React.FC<{ initialTab?: string; target?: AdminTargetIn }> = ({ initialTab, target }) => {
  const start = initialTab && TABS.includes(initialTab) ? initialTab : TABS[0];
  const [tab, setTab] = useState(start);
  // Each tab mounts on its first visit and then stays mounted (hidden), so its
  // filters and open edits survive flipping between tabs.
  const [seen, setSeen] = useState<Record<string, boolean>>({ [start]: true });
  const [range, setRange] = useState<Range>(defaultRange);
  const pick = (t: string) => { setTab(t); setSeen(v => (v[t] ? v : { ...v, [t]: true })); };
  useEffect(() => { if (initialTab && TABS.includes(initialTab)) pick(initialTab); }, [initialTab]);

  // A deep link's page drawer lives here, not in a table: the page need not be
  // on the table's current page (or match its filters) to be opened.
  const [linked, setLinked] = useState<PageRef | null>(null);
  useEffect(() => {
    if (!target) return;
    const raw = String(target.tab || '');
    const t = TABS.includes(raw) ? raw : TAB_PARAM[raw.toLowerCase()];
    const id = String(target.target || target.tech || '').trim();
    const kind = target.kind === 'university' || (!target.kind && t === 'University pages') ? 'university' : 'tech';
    if (t) pick(t);
    else if (id) pick(kind === 'university' ? 'University pages' : 'Tech pages');
    if (id) setLinked({ kind, target: id });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target?.nonce]);

  // One read of the universities sheet feeds the page tables' university /
  // country filters and the Labels & carousel tab — fetched when first needed.
  const needSheet = !!(seen['Tech pages'] || seen['University pages'] || seen['Labels & carousel']);
  const [sheet, setSheet] = useState<{ data: any; loading: boolean; error: string }>({ data: null, loading: true, error: '' });
  const seq = useRef(0);
  const loadSheet = useCallback(async () => {
    const mine = ++seq.current;
    setSheet(s => ({ ...s, loading: true, error: '' }));
    const { data, error } = await panelGet('universities', 'sheet');
    if (mine === seq.current) setSheet({ data, loading: false, error });
  }, []);
  useEffect(() => { if (needSheet && seq.current === 0) loadSheet(); }, [needSheet, loadSheet]);
  const sheetRes = useMemo(() => ({ ...sheet, reload: loadSheet }), [sheet, loadSheet]);
  const universities = useMemo(() => (Array.isArray(sheetRes.data?.rows) ? sheetRes.data.rows : [])
    .map((r: any) => ({ code: String(r.code || ''), name: String(r.name || ''), label: String(r.website_label || ''), country: String(r.country || '') }))
    .filter((u: any) => u.code), [sheetRes.data]);

  const pane = (t: string, el: React.ReactNode) =>
    seen[t] ? <div key={t} className={tab === t ? 'flex flex-col gap-4' : 'hidden'}>{el}</div> : null;

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <SubTabs tabs={TABS} active={tab} onChange={pick} />
        <a href="/universities" target="_blank" rel="noopener noreferrer"
          className="text-xs text-primary-700 underline">Open the public universities page ↗</a>
      </div>
      {TRAFFIC.has(tab) && (
        <div className="rounded-xl border border-grayscale-300 bg-white px-4 py-2.5">
          <RangeBar value={range} onChange={setRange} />
        </div>
      )}
      {pane('Overview', <Overview range={range} onOpenTab={pick} />)}
      {pane('Tech pages', <PagesTable kind="tech" range={range} universities={universities} />)}
      {pane('University pages', <PagesTable kind="university" range={range} universities={universities} />)}
      {pane('Shares', <Shares range={range} />)}
      {pane('Sections & access', <SectionsAccess />)}
      {pane('URLs & SEO', <UrlsSeo />)}
      {pane('Labels & carousel', <LabelsCarousel sheet={sheetRes} />)}
      {linked && <PageDrawer page={linked} range={range} onClose={() => setLinked(null)} />}
    </div>
  );
};

export default TechPagesPanel;
